import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send } from "lucide-react";
import { api } from "@/lib/apiClient";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete name, email and message.");
      return;
    }
    setLoading(true);
    try {
      // Reuse bookings endpoint as a general lead capture (Contact = Quote lead)
      await api.post("/bookings", {
        name: form.name,
        email: form.email,
        phone: form.phone || "n/a",
        service_type: "Home Cleaning",
        address: "Contact form — address TBD",
        preferred_date: new Date().toISOString().slice(0, 10),
        preferred_time: "",
        message: `[Contact form] ${form.message}`,
      });
      toast.success("Message sent. We'll be in touch soon.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (_) {
      toast.error("Couldn't send. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="pc-section bg-white">
      <div className="pc-container grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
        <div className="lg:col-span-5">
          <div className="eyebrow text-[var(--pc-green-hover)]">Contact</div>
          <h2 className="font-display mt-3 text-4xl font-black tracking-tighter sm:text-5xl">
            Talk to a real human.
          </h2>
          <p className="mt-5 max-w-md text-base text-black/60">
            Toronto-based, GTA-wide. Pick whichever channel feels easiest.
          </p>

          <ul className="mt-10 space-y-5">
            <li className="flex items-start gap-4">
              <span className="grid h-11 w-11 place-items-center rounded-full bg-black text-[var(--pc-green)]">
                <Phone size={18} />
              </span>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-black/50">Call / Text</div>
                <a
                  href="tel:6476099266"
                  data-testid="contact-phone"
                  className="text-lg font-semibold hover:text-[var(--pc-green-hover)]"
                >
                  647-609-9266
                </a>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <span className="grid h-11 w-11 place-items-center rounded-full bg-black text-[var(--pc-green)]">
                <Mail size={18} />
              </span>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-black/50">Email</div>
                <a
                  href="mailto:pristyncleaning@gmail.com"
                  data-testid="contact-email"
                  className="text-lg font-semibold hover:text-[var(--pc-green-hover)]"
                >
                  pristyncleaning@gmail.com
                </a>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <span className="grid h-11 w-11 place-items-center rounded-full bg-black text-[var(--pc-green)]">
                <MapPin size={18} />
              </span>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-black/50">Service Area</div>
                <div className="text-lg font-semibold">Toronto + GTA</div>
                <div className="text-sm text-black/60">
                  Mississauga · Vaughan · Brampton · Markham · Richmond Hill · North York · Etobicoke · Scarborough
                </div>
                <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-black px-3 py-1.5 text-xs text-white">
                  <span className="h-1.5 w-1.5 rounded-full bg-[var(--pc-green)]" /> Mon–Sat · 8AM–7PM
                  <span className="ml-1 rounded-full bg-red-500/90 px-2 py-0.5 text-[10px] uppercase tracking-[0.15em]">Closed Sun</span>
                </div>
              </div>
            </li>
          </ul>
        </div>

        <form
          data-testid="contact-form"
          onSubmit={submit}
          className="lg:col-span-7 rounded-3xl bg-black p-6 text-white md:p-10"
        >
          <h3 className="font-display text-2xl font-bold tracking-tight md:text-3xl">
            Send a quick message
          </h3>
          <p className="mt-2 text-sm text-white/60">
            We reply by phone or email — your choice.
          </p>
          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
            <input
              data-testid="contact-input-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Your name *"
              className="pc-dark-input"
              required
            />
            <input
              data-testid="contact-input-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="Email *"
              className="pc-dark-input"
              required
            />
            <input
              data-testid="contact-input-phone"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="Phone (optional)"
              className="pc-dark-input md:col-span-2"
            />
            <textarea
              data-testid="contact-input-message"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="How can we help? *"
              className="pc-dark-input min-h-[140px] py-3 md:col-span-2"
              required
            />
          </div>
          <button
            data-testid="contact-submit"
            type="submit"
            disabled={loading}
            className="pc-btn-primary mt-6 w-full justify-center text-base disabled:opacity-60 md:w-auto"
          >
            {loading ? "Sending…" : "Send Message"} <Send size={16} />
          </button>
        </form>
      </div>

      <style>{`
        .pc-dark-input {
          width: 100%;
          background: rgba(255,255,255,0.06);
          border: 1px solid rgba(255,255,255,0.12);
          border-radius: 0.85rem;
          padding: 0.9rem 1rem;
          color: #fff;
          font-family: inherit;
          font-size: 0.95rem;
        }
        .pc-dark-input::placeholder { color: rgba(255,255,255,0.45); }
        .pc-dark-input:focus {
          outline: none;
          border-color: var(--pc-green);
          box-shadow: 0 0 0 4px rgba(0,230,118,0.18);
        }
      `}</style>
    </section>
  );
}
